// Live updates over WebSocket (GET /api/live) with reconnect.
// Browsers cannot set headers on a WebSocket, so the token travels as ?token= (plus client and tz).
// Server -> client: { t: 'state' | 'notify' }. Client -> server: { t: 'hello', client } on open, { t: 'seen' }.
// Heartbeat: plain-text 'ping' every 25 s (the server answers 'pong' without waking up); no traffic for 70 s means the
// socket is dead (NAT timeout, sleeping radio) -> close and reconnect. Close 4001/4002 (signed out / account deleted)
// stops reconnecting and calls onAuthClosed.
import { LIVE_CLOSE, type LiveClientMsg, type LiveServerMsg } from './types';
import { API_BASE, timeZone } from './client';
import { platform } from '../native';

export interface LiveSocket {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code?: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}
export type SocketFactory = (url: string) => LiveSocket;
let factory: SocketFactory = (url) => new WebSocket(url) as unknown as LiveSocket;
/** Replace the socket implementation (the mock backend does this). */
export function setSocketFactory(f: SocketFactory) { factory = f; }

export type LiveStatus = 'off' | 'connecting' | 'open' | 'retrying';

export function liveUrl(token: string): string {
  const base = API_BASE || (typeof location !== 'undefined' ? location.origin : 'http://localhost');
  const u = new URL(base.replace(/\/$/, '') + '/api/live');
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.searchParams.set('token', token);
  u.searchParams.set('client', platform());
  u.searchParams.set('tz', timeZone());
  return u.toString();
}

export interface LiveOptions {
  token: () => string | null;
  onMessage: (m: LiveServerMsg) => void;
  onStatus?: (s: LiveStatus) => void;
  /** Called after a reconnect (not the first open): resync with GET /api/state. */
  onReconnect?: () => void;
  /** The server closed with 4001 (signed out) or 4002 (account deleted). No more reconnects. */
  onAuthClosed?: (code: number) => void;
}

const PING_MS = 25000;
const DEAD_MS = 70000;

export interface LiveConnection { send(m: LiveClientMsg): void; close(): void; kick(): void }

export function connectLive(o: LiveOptions): LiveConnection {
  let ws: LiveSocket | null = null;
  let closed = false;
  let attempt = 0;
  let opened = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  let pinger: ReturnType<typeof setInterval> | null = null;

  const status = (s: LiveStatus) => o.onStatus?.(s);
  const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
  const arm = () => {
    if (watchdog) clearTimeout(watchdog);
    const sock = ws;
    watchdog = setTimeout(() => {
      // No message or pong for DEAD_MS: drop it (onclose may never come for a half-open socket) and reconnect.
      if (!sock || ws !== sock) return;
      ws = null;
      try { sock.onclose = null; sock.close(4000, 'stale'); } catch { /* ignore */ }
      stopPing();
      schedule();
    }, DEAD_MS);
  };
  const stopPing = () => {
    if (pinger) { clearInterval(pinger); pinger = null; }
    if (watchdog) { clearTimeout(watchdog); watchdog = null; }
  };

  const open = () => {
    clear();
    if (closed) return;
    const token = o.token();
    if (!token) { status('off'); return; }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) { status('retrying'); return; }
    status('connecting');
    let sock: LiveSocket;
    try { sock = factory(liveUrl(token)); } catch { schedule(); return; }
    ws = sock;
    sock.onopen = () => {
      const again = opened;
      opened = true; attempt = 0;
      status('open');
      try { sock.send(JSON.stringify({ t: 'hello', client: platform() } satisfies LiveClientMsg)); } catch { /* ignore */ }
      arm();
      if (pinger) clearInterval(pinger);
      pinger = setInterval(() => { try { if (sock.readyState === 1) sock.send('ping'); } catch { /* ignore */ } }, PING_MS);
      if (again) o.onReconnect?.();
    };
    sock.onmessage = (ev) => {
      arm();
      const data = String(ev.data);
      if (data === 'pong') return;
      let m: LiveServerMsg;
      try { m = JSON.parse(data) as LiveServerMsg; } catch { return; }
      if (m.t === 'ping') return;
      o.onMessage(m);
    };
    sock.onerror = () => { /* onclose follows */ };
    sock.onclose = (ev) => {
      if (ws !== sock) return; // replaced (stale socket dropped by the watchdog)
      ws = null;
      stopPing();
      const code = ev?.code;
      if (code === LIVE_CLOSE.signedOut || code === LIVE_CLOSE.deleted) { closed = true; status('off'); o.onAuthClosed?.(code); return; }
      if (!closed) schedule();
    };
  };

  const schedule = () => {
    if (closed) return;
    status('retrying');
    const base = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
    attempt++;
    clear();
    timer = setTimeout(open, base * (0.75 + Math.random() * 0.5));
  };

  const kick = () => { if (!closed && (!ws || ws.readyState > 1)) { attempt = 0; open(); } };
  const onOnline = () => kick();
  const onVisible = () => { if (document.visibilityState === 'visible') kick(); };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
  }
  open();

  return {
    send(m) { try { if (ws && ws.readyState === 1) ws.send(JSON.stringify(m)); } catch { /* ignore */ } },
    kick,
    close() {
      closed = true; clear();
      stopPing();
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
        document.removeEventListener('visibilitychange', onVisible);
      }
      const sock = ws;
      ws = null;
      try { sock?.close(1000, 'bye'); } catch { /* ignore */ }
      status('off');
    },
  };
}
